import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Button, CircularProgress, TextField } from '@mui/material';
import PostData from '../services/PostData.js';
import useApiData from '../services/useApiData.js';
import SnackbarEx from '../components/Helpers/SnackbarEx.jsx';

const ITEM_ID_CATALOG_ENDPOINT = 'api/broylesllc/shelf-inventory/item-id-catalog';
const SUBMIT_SHELF_INVENTORY_ENDPOINT = 'broylesllc/shelf-inventory/submit';
const MIN_ITEM_ID_SEARCH_LENGTH = 2;
const MAX_ITEM_ID_RESULTS = 100;

const getItemIdLabel = (option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return String(option.itemId || option.id || '');
};

const getItemIdKey = (option) => option?.id || getItemIdLabel(option);

const getSelectedItemPayload = (option) => ({
    itemId: getItemIdLabel(option),
    ids: option?.id ? [option.id] : [],
    skuName: option?.skuName || '',
});

const getSubmitErrorMessage = (error) => {
    const responseError = error?.response?.data?.message || error?.response?.data;

    return typeof responseError === 'string'
        ? responseError
        : 'Unable to submit shelf inventory.';
};

export default function ShelfInventory() {
    const [shelfName, setShelfName] = useState('');
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [itemIdSearch, setItemIdSearch] = useState('');
    const [debouncedItemIdSearch, setDebouncedItemIdSearch] = useState('');
    const [submitLoad, setSubmitLoad] = useState(false);
    const [openTestSnack, setOpenTestSnack] = useState({
        color: '',
        message: '',
        open: false
    });

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setDebouncedItemIdSearch(itemIdSearch.trim());
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [itemIdSearch]);

    const canSearchItemIds = debouncedItemIdSearch.length >= MIN_ITEM_ID_SEARCH_LENGTH;
    const itemIdCatalogEndpoint = useMemo(() => (
        `${ITEM_ID_CATALOG_ENDPOINT}?search=${encodeURIComponent(debouncedItemIdSearch)}&limit=${MAX_ITEM_ID_RESULTS}`
    ), [debouncedItemIdSearch]);
    const { data: itemIdCatalog, error: itemIdCatalogError, load: loadingItemIdCatalog } = useApiData(
        itemIdCatalogEndpoint,
        { enabled: canSearchItemIds }
    );

    const itemIdOptions = useMemo(() => (
        Array.isArray(itemIdCatalog) ? itemIdCatalog : []
    ), [itemIdCatalog]);

    const filteredItemIdOptions = useMemo(() => {
        if (itemIdSearch.trim().length < MIN_ITEM_ID_SEARCH_LENGTH) return [];
        return itemIdOptions.slice(0, MAX_ITEM_ID_RESULTS);
    }, [itemIdOptions, itemIdSearch]);

    useEffect(() => {
        if (loadingItemIdCatalog || filteredItemIdOptions.length !== 1) {
            return;
        }

        const onlyMatch = filteredItemIdOptions[0];
        const alreadySelected = selectedItemIds.some(
            (s) => getItemIdKey(s) === getItemIdKey(onlyMatch)
        );

        if (!alreadySelected) {
            setSelectedItemIds((prev) => [...prev, onlyMatch]);
        }

        setItemIdSearch('');
    }, [filteredItemIdOptions, loadingItemIdCatalog]);

    const submitDisabled = submitLoad || !shelfName.trim() || selectedItemIds.length === 0;

    const handleSubmit = async () => {
        if (submitDisabled) {
            return;
        }

        setSubmitLoad(true);

        const payload = {
            shelfName: shelfName.trim(),
            itemIdCatalog: selectedItemIds.map(getSelectedItemPayload),
        };

        const { success, error } = await PostData(payload, SUBMIT_SHELF_INVENTORY_ENDPOINT, null);

        setSubmitLoad(false);

        if (success) {
            setShelfName('');
            setSelectedItemIds([]);
            setItemIdSearch('');
            setDebouncedItemIdSearch('');
            setOpenTestSnack({
                color: 'success',
                message: 'Shelf inventory submitted successfully.',
                open: true
            });
            return;
        }

        setOpenTestSnack({
            color: 'error',
            message: getSubmitErrorMessage(error),
            open: true
        });
    };

    return (
        <>
            <div className="card border-0" style={{
                height: 'calc(100vh - 56px)',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <div className="card-header bg-white"
                    style={{
                        padding: '0 20px 5px 20px',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        borderBottom: '1px solid #eee'
                    }}>
                    <h2 style={{ margin: 0, paddingTop: '10px' }}>Take Shelf Inventory</h2>
                    <p style={{ margin: 0 }}>Flag once inventory completed for deletion of old stock</p>
                </div>
                <div className="card-body" style={{
                    flex: 1,
                    overflowY: 'auto',
                    margin: 0,
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '720px',
                    }}>
                        <TextField
                            label="Shelf Name"
                            value={shelfName}
                            onChange={(event) => setShelfName(event.target.value)}
                            size="small"
                            fullWidth
                        />
                        <Autocomplete
                            multiple
                            loading={loadingItemIdCatalog}
                            options={filteredItemIdOptions}
                            value={selectedItemIds}
                            inputValue={itemIdSearch}
                            onChange={(event, newValue) => setSelectedItemIds(newValue)}
                            onInputChange={(event, newInputValue) => setItemIdSearch(newInputValue)}
                            getOptionLabel={getItemIdLabel}
                            isOptionEqualToValue={(option, value) => getItemIdKey(option) === getItemIdKey(value)}
                            filterOptions={(options) => options}
                            renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                return (
                                    <li key={key} {...rest}>
                                        <div>
                                            <div>{getItemIdLabel(option)}</div>
                                            {option?.skuName ? (
                                                <div style={{ fontSize: 12, color: '#5c6570' }}>{option.skuName}</div>
                                            ) : null}
                                        </div>
                                    </li>
                                );
                            }}
                            loadingText="Fetching item IDs..."
                            noOptionsText={
                                loadingItemIdCatalog
                                    ? 'Fetching item IDs'
                                    : itemIdSearch.trim().length < MIN_ITEM_ID_SEARCH_LENGTH
                                        ? `Type at least ${MIN_ITEM_ID_SEARCH_LENGTH} characters to search`
                                        : 'No item IDs found'
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Item ID Catalog"
                                    size="small"
                                    error={Boolean(itemIdCatalogError)}
                                    helperText={
                                        itemIdCatalogError
                                            ? 'Unable to load item ID catalog.'
                                            : `Showing up to ${MAX_ITEM_ID_RESULTS} matching item IDs.`
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingItemIdCatalog && (
                                                    <CircularProgress color="inherit" size={20} />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="card-footer" style={{
                    padding: '16px 20px',
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    background: 'white'
                }}>
                    <Button
                        variant="contained"
                        loading={submitLoad}
                        disabled={submitDisabled}
                        disableElevation
                        onClick={handleSubmit}
                        sx={{
                            backgroundColor: '#3E5C76',
                            width: '200px',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#3e5c76f0' }
                        }}
                    >
                        Submit
                    </Button>
                </div>
            </div>
            {openTestSnack.open && (
                <SnackbarEx
                    color={openTestSnack.color}
                    message={openTestSnack.message}
                    open={openTestSnack.open}
                    close={() => setOpenTestSnack({
                        color: '',
                        message: '',
                        open: false
                    })}
                />
            )}
        </>
    );
};
